import { codeFont, numFont, plateFont } from '../../utils/fonts';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HistoryIcon from '@mui/icons-material/History';
import PaymentsIcon from '@mui/icons-material/Payments';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '../../store';
import PartAutocomplete from '../shared/PartAutocomplete';
import CustomerAutocomplete from '../shared/CustomerAutocomplete';
import CustomerDialog from '../customers/CustomerDialog';
import CustomerPurchaseHistoryDialog from '../customers/CustomerPurchaseHistoryDialog';
import PaymentDialog from './PaymentDialog';
import QuickPickManageDialog from './QuickPickManageDialog';
import SaleLineDialog from './SaleLineDialog';
import TargetLabelField from './TargetLabelField';
import { formatPriceWithCurrency } from '../../utils/vat';
import { formatQuantity } from '../../utils';
import { can } from '../../utils/access';
import {
  TERMINAL_RETRY_ATTEMPTS,
  isTransientTerminalCondition,
  waitBeforeTerminalRetry,
} from '../../utils/terminalErrors';
import {
  displayUnitPrice,
  emptyFreeTextLine,
  estimateCartTotals,
  estimateLine,
  jobNeedsPriceEntry,
  lineDiscountAmount,
  lineFromJob,
  lineFromPart,
  lineFromQuickPick,
  toLineSpec,
  totalDiscountAmount,
  totalMargin,
} from './salesUtils';

function Kbd({ children }) {
  return (
    <Box
      component="span"
      sx={{
        px: 0.75,
        py: 0.125,
        border: '1px solid',
        borderColor: 'divider',
        borderBottomWidth: 2,
        borderRadius: '5px',
        fontSize: 11,
        fontFamily: 'monospace',
        bgcolor: 'grey.50',
        color: 'text.secondary',
      }}
    >
      {children}
    </Box>
  );
}

function SumRow({ label, value, muted }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="body2" color={muted ? 'text.secondary' : 'text.primary'}>{label}</Typography>
      <Typography variant="body2" sx={{ ...numFont }} color={muted ? 'text.secondary' : 'text.primary'}>
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Kassamyynti — barcode-first fast-entry till, per claude_designs/osamyynti.
 * Left: scan field, quick picks, cart. Right (sticky): customer, totals, pay.
 * F2 opens the payment flow, Esc clears the cart. Settlement creates a paid
 * invoice in the background — nothing blocks the counter.
 */
function CounterSaleView() {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const [lines, setLines] = useState([]);
  const [customer, setCustomer] = useState(null);
  // Kohde (target vehicle/machine) for the whole cart: stamped onto each line as it is
  // added, so a line added before the seller typed it keeps its own (blank) value.
  const [cartTarget, setCartTarget] = useState('');
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [terminals, setTerminals] = useState([]);
  const [terminalId, setTerminalId] = useState('');
  const [quickPicks, setQuickPicks] = useState([]);
  const [managePicksOpen, setManagePicksOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  // An unpaid counter sale left behind by a failed card capture — settlement
  // retries and void must target THIS sale, never create a second one.
  const [pendingSale, setPendingSale] = useState(null);
  const [editLine, setEditLine] = useState(null);
  // A job without a usable price (variable / 0 €) is priced in the line dialog
  // before it lands in the cart.
  const [newJobLine, setNewJobLine] = useState(null);
  const [flashKey, setFlashKey] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'error' });
  const settledCountRef = useRef(0);

  const terminalEnabled = currentOrganization?.payment_terminal_enabled;
  // Only a Verstaani-integrated (Nexi) terminal captures the card itself. Without
  // one, "card" is recorded manually (charged on the workshop's own device) — a
  // single atomic request, same as cash.
  const hasTerminal = !!terminalEnabled && terminals.length > 0 && !!terminalId;
  // Org display convention: show VAT-inclusive prices everywhere when on.
  const orgDefaultVat = currentOrganization?.default_price_includes_vat ?? false;
  const vatSuffix = (inclVat) => t(inclVat ? 'incl. VAT' : 'excl. VAT');

  useEffect(() => {
    if (!terminalEnabled) return;
    axios
      .get('/api/payment-terminals/')
      .then((r) => {
        const data = r.data.results || r.data;
        const active = (Array.isArray(data) ? data : []).filter((x) => x.is_active);
        setTerminals(active);
        if (active.length > 0) setTerminalId(active[0].id);
      })
      .catch(() => setTerminals([]));
  }, [terminalEnabled]);

  const fetchQuickPicks = useCallback(() => {
    axios
      .get('/api/parts-sales-quick-picks/')
      .then((r) => setQuickPicks(Array.isArray(r.data) ? r.data : []))
      .catch(() => setQuickPicks([]));
  }, []);

  useEffect(() => {
    fetchQuickPicks();
  }, [fetchQuickPicks]);

  const flash = (key) => {
    setFlashKey(key);
    setTimeout(() => setFlashKey(null), 700);
  };

  // Account customers' default line discounts (asiakaskohtaiset alennukset):
  // parts_discount_percent for part and free-text lines, jobs_discount_percent
  // for job lines — the same defaults the backend line factory applies, so the
  // till matches the invoice. Visible in the cart, overridable per line.
  const pct = (v) => (parseFloat(v) > 0 ? String(parseFloat(v)) : '0');
  const customerDiscountsOf = (c) => ({ parts: pct(c?.parts_discount_percent), jobs: pct(c?.jobs_discount_percent) });
  const defaultDiscountFor = (line, d) => (line.job_id ? d.jobs : d.parts);
  const discounts = customerDiscountsOf(customer);

  const attachCustomer = (c) => {
    setCustomer(c);
    setCustomerPickerOpen(false);
    const d = customerDiscountsOf(c);
    if (d.parts !== '0' || d.jobs !== '0') {
      // Fill the discount into lines still at 0 — never stomp a manual edit.
      setLines((prev) =>
        prev.map((l) => {
          const def = defaultDiscountFor(l, d);
          return def !== '0' && (parseFloat(l.discount_percent) || 0) === 0 ? { ...l, discount_percent: def } : l;
        })
      );
    }
  };

  const detachCustomer = () => {
    const d = customerDiscountsOf(customer);
    if (d.parts !== '0' || d.jobs !== '0') {
      // Reset only lines still carrying exactly this customer's default discount.
      setLines((prev) =>
        prev.map((l) => {
          const def = parseFloat(defaultDiscountFor(l, d)) || 0;
          return def > 0 && parseFloat(l.discount_percent) === def ? { ...l, discount_percent: '0' } : l;
        })
      );
    }
    setCustomer(null);
  };

  const mergeLine = (newLine) => {
    setLines((prev) => {
      // Same part / same job already in the cart → bump its quantity (+1 h for
      // an hourly job, consistent with the ±1 stepper).
      const matchKey = newLine.part_id ? 'part_id' : newLine.job_id ? 'job_id' : null;
      if (matchKey) {
        const existing = prev.find((l) => l[matchKey] === newLine[matchKey]);
        if (existing) {
          flash(existing.key);
          return prev.map((l) =>
            l[matchKey] === newLine[matchKey]
              ? { ...l, quantity: String((parseFloat(l.quantity) || 0) + 1) }
              : l
          );
        }
      }
      flash(newLine.key);
      return [...prev, { ...newLine, target_label: newLine.target_label || cartTarget.trim() }];
    });
  };

  // Jobs without a usable price (variable-priced / 0 €) open the line dialog
  // first — never silently appended at 0 €. Everything else merges straight in.
  const addResolvedLine = (line) => (jobNeedsPriceEntry(line) ? setNewJobLine(line) : mergeLine(line));
  const addPart = (part) => mergeLine(lineFromPart(part, discounts.parts));
  const addJob = (job) => addResolvedLine(lineFromJob(job, discounts.jobs));
  const addQuickPick = (pick) =>
    addResolvedLine(lineFromQuickPick(pick, pick.kind === 'job' ? discounts.jobs : discounts.parts));

  const updateLine = (key, field, val) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: val } : l)));
  };
  const stepQty = (key, delta) => {
    setLines((prev) =>
      prev
        .map((l) => {
          if (l.key !== key) return l;
          const q = (parseFloat(l.quantity) || 0) + delta;
          return { ...l, quantity: String(q) };
        })
        .filter((l) => parseFloat(l.quantity) > 0)
    );
  };
  const removeLine = (key) => setLines((prev) => prev.filter((l) => l.key !== key));
  const clearCart = useCallback(() => {
    setLines([]);
    setCustomer(null);
    setCustomerPickerOpen(false);
  }, []);

  const totals = estimateCartTotals(lines);
  // Kate is foreman+ (costs.view_margins) and needs the cost itself, which the API
  // strips without costs.view_purchase_prices. Osamyynti is already foreman+, so in
  // practice everyone at the till sees it unless an admin turned it off for the role.
  const canViewMargins =
    can(currentOrganization, 'costs.view_purchase_prices') && can(currentOrganization, 'costs.view_margins');
  const totalDiscount = totalDiscountAmount(lines, orgDefaultVat);
  const cartMargin = canViewMargins ? totalMargin(lines) : null;

  // "−30 % (−16,68 €)". The row total already has the discount baked in, so
  // without this the seller cannot tell a discounted row from a cheap one.
  const discountCaption = (line) => {
    const pct = parseFloat(line.discount_percent) || 0;
    if (pct <= 0) return null;
    const amount = lineDiscountAmount(line, orgDefaultVat);
    const pctLabel = `−${formatQuantity(pct)} %`;
    return amount ? `${pctLabel} (−${formatPriceWithCurrency(amount)})` : pctLabel;
  };
  // Hours are not pieces: an hourly job counts as one item however long it takes.
  const cartCount = lines.reduce((s, l) => s + (l.unit === 'h' ? 1 : parseFloat(l.quantity) || 0), 0);
  const canSettle = lines.length > 0;

  // Keyboard shortcuts: F2 = pay, Esc = clear (only when no dialog is open).
  useEffect(() => {
    const handler = (e) => {
      if (payOpen || managePicksOpen || editLine || newJobLine) return;
      if (e.key === 'F2' && canSettle) {
        e.preventDefault();
        setPayOpen(true);
      } else if (e.key === 'Escape' && lines.length > 0 && !pendingSale) {
        e.preventDefault();
        clearCart();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [payOpen, managePicksOpen, editLine, newJobLine, canSettle, lines.length, clearCart, pendingSale]);

  /**
   * Take ONE tender on the sale in progress, creating the sale first if needed.
   *
   * This is what makes two cards possible at the till: each tap is its own request, so
   * a decline on the second leaves the first one's money recorded instead of unwinding
   * a charge that really happened. Returns the same shape as `settle`, plus `remaining`.
   */
  /**
   * POST a terminal step, retrying a few times while the reader is merely busy finishing
   * the previous transaction. Each attempt is its own request and captures nothing on a
   * busy answer, so retrying is safe; `onRetry` lets the dialog say so.
   */
  const postWithBusyRetry = async (url, body, onRetry) => {
    for (let attempt = 1; ; attempt += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        return await axios.post(url, body, { timeout: 60000 });
      } catch (e) {
        if (attempt < TERMINAL_RETRY_ATTEMPTS && isTransientTerminalCondition(e.response?.data?.terminal_result)) {
          if (onRetry) onRetry(attempt + 1, TERMINAL_RETRY_ATTEMPTS);
          // eslint-disable-next-line no-await-in-loop
          await waitBeforeTerminalRetry();
          continue;
        }
        throw e;
      }
    }
  };

  const settleLeg = async (leg, expectedRemaining = null, onRetry = null) => {
    let sale = pendingSale;
    let expected = expectedRemaining;
    if (!sale) {
      // Commit the sale and its draft invoice first, so the blocking terminal call in
      // the next request holds only the invoice row.
      const createResp = await axios.post('/api/parts-sales/counter_sale/', {
        lines: lines.map(toLineSpec),
        ...(customer ? { customer_id: customer.id } : {}),
        ...(cartTarget.trim() ? { target_label: cartTarget.trim() } : {}),
        defer_settlement: true,
      });
      sale = createResp.data;
      setPendingSale(sale);
      // A fresh sale owes exactly the server's total. The cart's own arithmetic can be a
      // cent off, which would fail the very first leg's expected_remaining check.
      if (expected != null && sale.invoice?.total_incl_vat != null) expected = sale.invoice.total_incl_vat;
    }
    const resp = await postWithBusyRetry(
      `/api/parts-sales/${sale.sale.id}/payment_leg/`,
      {
        method: leg.method,
        amount: parseFloat(leg.amount),
        ...(expected != null ? { expected_remaining: expected } : {}),
        ...(leg.method === 'card' && hasTerminal ? { terminal_id: terminalId } : {}),
      },
      onRetry,
    );
    const data = { ...sale, ...resp.data };
    if (data.settled) {
      settledCountRef.current = cartCount;
      setLines([]);
      setCustomer(null);
      setCartTarget('');
      setCustomerPickerOpen(false);
      setPendingSale(null);
    } else {
      setPendingSale(data);
    }
    return data;
  };

  const settle = async (paymentMethod, payments = null) => {
    // Split (cash+card legs) sends `payments` + terminal; single-method sends the
    // legacy `payment_method` field. The backend treats `payments` as authoritative.
    const paymentFields = payments && payments.length
      ? { payments, ...(hasTerminal ? { terminal_id: terminalId } : {}) }
      : {
          payment_method: paymentMethod,
          ...(paymentMethod === 'card' && hasTerminal ? { terminal_id: terminalId } : {}),
        };
    let data;
    if (pendingSale) {
      // Retry settlement of the existing unpaid sale — no new sale, no new deduction.
      const resp = await axios.post(`/api/parts-sales/${pendingSale.sale.id}/settle_payment/`, paymentFields);
      data = resp.data;
    } else if (hasTerminal && (paymentMethod === 'card' || (payments && payments.length))) {
      // Two-phase terminal tender: create the sale committed first, then run
      // the capture in its own request so the blocking Nexi call holds only
      // the invoice row lock (not the org counters / inventory rows).
      const createResp = await axios.post('/api/parts-sales/counter_sale/', {
        lines: lines.map(toLineSpec),
        ...(customer ? { customer_id: customer.id } : {}),
        ...(cartTarget.trim() ? { target_label: cartTarget.trim() } : {}),
        defer_settlement: true,
      });
      setPendingSale(createResp.data);
      try {
        const resp = await axios.post(`/api/parts-sales/${createResp.data.sale.id}/settle_payment/`, paymentFields);
        data = resp.data;
      } catch (e) {
        // The sale exists server-side — land on the resumable failed stage
        // instead of throwing away the context. Keep the response body: it carries
        // terminal_result, which is the only thing that tells the seller what to fix
        // on the reader.
        data = { ...createResp.data, ...(e.response?.data || {}), settled: false };
      }
    } else {
      const payload = {
        lines: lines.map(toLineSpec),
        ...(customer ? { customer_id: customer.id } : {}),
        ...(cartTarget.trim() ? { target_label: cartTarget.trim() } : {}),
        ...paymentFields,
      };
      const resp = await axios.post('/api/parts-sales/counter_sale/', payload);
      data = resp.data;
    }
    if (data.settled) {
      settledCountRef.current = cartCount;
      setLines([]);
      setCustomer(null);
      setCartTarget('');
      setCustomerPickerOpen(false);
      setPendingSale(null);
    } else {
      // Keep the cart on screen — the sale isn't complete until it's paid or voided.
      setPendingSale(data);
    }
    return data;
  };

  // Map a line-spec emitted by SaleLineDialog back onto a cart line. Catalogue-
  // backed lines (part or job) keep their snapshotted VAT scheme; free lines
  // follow the dialog's margin choice.
  const applySpecToLine = (l, spec) => {
    const catalogueBacked = !!(l.part_id || l.job_id);
    return {
      ...l,
      quantity: String(spec.quantity),
      unit_price: String(spec.unit_price),
      unit_price_incl_vat: spec.is_margin_vat_scheme
        ? String(spec.unit_price)
        : spec.unit_price_incl_vat != null
          ? String(spec.unit_price_incl_vat)
          : null,
      price_entered_with_vat: !!spec.price_entered_with_vat,
      discount_percent: String(spec.discount_percent),
      description: spec.description,
      target_label: spec.target_label ?? l.target_label ?? '',
      is_margin_vat_scheme: catalogueBacked ? l.is_margin_vat_scheme : !!spec.is_margin_vat_scheme,
      margin_vat_purchase_price: catalogueBacked ? l.margin_vat_purchase_price : spec.margin_vat_purchase_price || null,
    };
  };

  /** Give one recorded leg of the pending sale back: card on the reader, cash by hand. */
  const refundLeg = async (legId, customerNotPresent = false, onRetry = null) => {
    const resp = await postWithBusyRetry(
      `/api/parts-sales/${pendingSale.sale.id}/payment_leg/${legId}/refund/`,
      { customer_not_present: customerNotPresent },
      onRetry,
    );
    const data = { ...pendingSale, ...resp.data };
    setPendingSale(data);
    return data;
  };

  const voidPendingSale = async (cashBack = 0) => {
    await axios.post(`/api/parts-sales/${pendingSale.sale.id}/void/`);
    setPendingSale(null);
    setPayOpen(false);
    setSnack({
      open: true,
      // Cash the customer already handed over is not in any system; the seller returns it.
      message: cashBack > 0
        ? t('parts_sales.voidedReturnCash', { amount: formatPriceWithCurrency(cashBack) })
        : t('parts_sales.voidedMessage'),
      severity: 'success',
    });
  };

  const startNewSale = () => {
    setPayOpen(false);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1240, mx: 'auto' }} data-testid="counter-sale-view">
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <PointOfSaleIcon color="secondary" />
        <Typography variant="h4" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
          {t('parts_sales.counter_sale')}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        {t('parts_sales.counterSaleSubtitle')}
      </Typography>

      {/* minmax(0, …) so the cart table can't force the tracks (and the page)
          wider than the viewport — it scrolls inside its own wrapper instead. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.55fr) minmax(0, 1fr)' }, gap: 2.5, alignItems: 'start' }}>
        {/* LEFT — scan + quick picks + cart */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <PartAutocomplete
            onSelect={addPart}
            onSelectJob={addJob}
            placeholder={t('parts_sales.scanOrPickPlaceholder')}
            autoFocus
            big
            sourceToggle
            catalogueFilters
            filtersCollapsible
            stockMatchCheck
            jobsToggle
            inputTestId="counter-sale-part-input"
          />

          {/* Quick picks */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>
                {t('parts_sales.quickPicks')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {quickPicks.length > 0 && (
                  <Typography variant="caption" color="text.disabled">
                    {t('parts_sales.pricesLabel')} {vatSuffix(orgDefaultVat)}
                  </Typography>
                )}
                <Tooltip title={t('parts_sales.manageQuickPicks')}>
                  <IconButton size="small" onClick={() => setManagePicksOpen(true)} data-testid="quick-pick-manage">
                    <TuneIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            {quickPicks.length > 0 ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: 1 }}>
                {quickPicks.map((pick) => {
                  const isJob = pick.kind === 'job';
                  return (
                    <Paper
                      key={pick.id}
                      component="button"
                      onClick={() => addQuickPick(pick)}
                      data-testid={`quick-pick-${pick.id}`}
                      elevation={0}
                      sx={{
                        textAlign: 'left',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '10px',
                        p: 1.25,
                        cursor: 'pointer',
                        bgcolor: 'background.paper',
                        fontFamily: 'inherit',
                        transition: 'border-color 150ms, box-shadow 150ms',
                        '&:hover': { borderColor: 'secondary.main', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
                      }}
                    >
                      <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        {isJob && (
                          <Chip label={t('parts_sales.jobTag')} size="small" color="secondary" variant="outlined" sx={{ height: 16, fontSize: '9.5px' }} />
                        )}
                        {isJob ? pick.job_name : pick.part_name}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ ...codeFont }} noWrap>
                          {isJob ? pick.job_code : pick.part_code}
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, ...numFont, flexShrink: 0 }}>
                          {formatPriceWithCurrency(parseFloat(
                            pick.is_margin_vat_scheme || orgDefaultVat
                              ? pick.unit_price_incl_vat ?? pick.unit_price
                              : pick.unit_price
                          ))}
                          {pick.unit === 'h' ? `/${t('parts_sales.hourUnit')}` : ''}
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            ) : (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setManagePicksOpen(true)}
                sx={{ color: 'text.secondary', textTransform: 'none' }}
              >
                {t('parts_sales.addQuickPicksHint')}
              </Button>
            )}
          </Box>

          {/* Cart */}
          <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: lines.length ? '1px solid' : 'none', borderColor: 'divider' }}>
              <Typography sx={{ fontWeight: 600 }}>
                {t('parts_sales.cart')}
                {lines.length > 0 && (
                  <Typography component="span" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {' '}· {cartCount} {t('parts_sales.pcs')}
                  </Typography>
                )}
              </Typography>
              {lines.length > 0 && (
                <Button size="small" startIcon={<DeleteSweepIcon />} onClick={clearCart} sx={{ color: 'text.secondary', textTransform: 'none' }}>
                  {t('parts_sales.clearCart')}
                </Button>
              )}
            </Box>
            {lines.length === 0 ? (
              <Box sx={{ py: 6, px: 3, textAlign: 'center' }}>
                <QrCodeScannerIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                <Typography sx={{ fontWeight: 600, mt: 1 }}>{t('parts_sales.scanFirstItem')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('parts_sales.stockDeductedOnAdd')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 480 }}>
                  <TableBody>
                    {lines.map((l) => (
                      <TableRow
                        key={l.key}
                        sx={{
                          bgcolor: flashKey === l.key ? '#ebf4ff' : 'transparent',
                          transition: 'background 600ms',
                          '&:last-child td': { borderBottom: 0 },
                        }}
                      >
                        <TableCell sx={{ py: 1.25 }}>
                          {l.part_id || l.job_id ? (
                            <>
                              <Typography variant="body2" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                {l.job_id && (
                                  <Chip
                                    label={t('parts_sales.jobTag')}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '10px' }}
                                    data-testid="cart-line-job-tag"
                                  />
                                )}
                                {l.description}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ...codeFont }}
                                data-testid="cart-line-caption"
                              >
                                {(() => {
                                  const unit = displayUnitPrice(l, orgDefaultVat);
                                  const perUnit = l.unit === 'h' ? t('parts_sales.hourUnit') : t('parts_sales.each');
                                  return [
                                    l.part_code,
                                    `${formatPriceWithCurrency(unit.value)}/${perUnit} ${vatSuffix(unit.inclVat)}`,
                                    discountCaption(l),
                                  ]
                                    .filter(Boolean)
                                    .join(' · ');
                                })()}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <TextField
                                variant="standard"
                                size="small"
                                fullWidth
                                placeholder={t('parts_sales.description')}
                                value={l.description}
                                onChange={(e) => updateLine(l.key, 'description', e.target.value)}
                              />
                              {discountCaption(l) && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ...numFont }}
                                  data-testid="cart-line-discount"
                                >
                                  {discountCaption(l)}
                                </Typography>
                              )}
                            </>
                          )}
                          {l.target_label && (
                            <Chip
                              size="small"
                              variant="outlined"
                              icon={<DirectionsCarIcon sx={{ fontSize: 13 }} />}
                              label={l.target_label}
                              sx={{ height: 20, fontSize: 10.5, mt: 0.5, ...plateFont }}
                              data-testid="cart-line-target"
                            />
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ width: 120 }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
                            <IconButton size="small" onClick={() => stepQty(l.key, -1)} aria-label={t('parts_sales.decreaseQty')}>
                              <RemoveIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <Typography sx={{ minWidth: 32, px: 0.5, textAlign: 'center', ...numFont, fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap' }}>
                              {parseFloat(l.quantity) || 0}
                              {l.unit === 'h' ? ` ${t('parts_sales.hourUnit')}` : ''}
                            </Typography>
                            <IconButton size="small" onClick={() => stepQty(l.key, 1)} aria-label={t('parts_sales.increaseQty')}>
                              <AddIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ width: 100, ...numFont, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {(() => {
                            const est = estimateLine(l);
                            return formatPriceWithCurrency(
                              est.margin || orgDefaultVat ? est.incl : est.excl
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right" sx={{ width: 84, whiteSpace: 'nowrap' }}>
                          <Tooltip title={t('parts_sales.editLine')}>
                            <IconButton size="small" onClick={() => setEditLine(l)} data-testid="counter-sale-edit-line">
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <IconButton size="small" onClick={() => removeLine(l.key)} aria-label={t('Delete')}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>

          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setLines((prev) => [...prev, emptyFreeTextLine(orgDefaultVat, discounts.parts)])}
            data-testid="counter-sale-free-line"
            sx={{ alignSelf: 'flex-start', color: 'text.secondary', textTransform: 'none' }}
          >
            {t('parts_sales.freeTextLine')}
          </Button>
        </Box>

        {/* RIGHT — customer + totals + pay */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: { lg: 'sticky' }, top: { lg: 24 } }}>
          {/* Customer */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
              {t('parts_sales.customer')}
            </Typography>
            {customer ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 38, height: 38, bgcolor: 'primary.main', fontSize: 13, fontWeight: 600 }}>
                  {(customer.name || '?').slice(0, 2).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{customer.name}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {customer.type === 'company' ? t('parts_sales.billingCustomer') : t('parts_sales.privateCustomer')}
                    {customer.email ? ` · ${customer.email}` : ''}
                  </Typography>
                </Box>
                <Tooltip title={t('Purchase history')}>
                  <IconButton size="small" onClick={() => setHistoryOpen(true)} data-testid="counter-sale-history">
                    <HistoryIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={detachCustomer} aria-label={t('parts_sales.detachCustomer')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : customerPickerOpen ? (
              <Box>
                <CustomerAutocomplete
                  value={null}
                  onChange={(c) => {
                    if (c) attachCustomer(c);
                    else setCustomerPickerOpen(false);
                  }}
                  autoFocus
                  placeholder={t('parts_sales.searchCustomer')}
                />
                <Button
                  size="small"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setNewCustomerOpen(true)}
                  data-testid="counter-sale-new-customer"
                  sx={{ mt: 1, textTransform: 'none', color: 'text.secondary' }}
                >
                  {t('New customer')}
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Avatar sx={{ width: 38, height: 38, bgcolor: 'grey.100', color: 'text.secondary' }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{t('parts_sales.walkInCustomer')}</Typography>
                </Box>
                <Button variant="outlined" size="small" startIcon={<PersonAddIcon />} onClick={() => setCustomerPickerOpen(true)} sx={{ borderRadius: '8px' }} data-testid="counter-sale-attach-customer">
                  {t('parts_sales.attachCustomer')}
                </Button>
              </Box>
            )}
          </Paper>

          {/* Kohde: which vehicle/machine the parts are for; prints under each row on the receipt */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
              {t('parts_sales.target')}
            </Typography>
            <TargetLabelField
              value={cartTarget}
              onChange={setCartTarget}
              customerId={customer?.id}
              placeholder={t('parts_sales.targetPlaceholder')}
              helperText={t('parts_sales.targetHelp')}
              inputTestId="counter-sale-target"
            />
          </Paper>

          {/* Totals + pay */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
            <SumRow label={t('parts_sales.netTotal')} value={formatPriceWithCurrency(totals.net)} />
            {totals.marginGross > 0 && (
              <SumRow label={t('parts_sales.marginLinesInclVat')} value={formatPriceWithCurrency(totals.marginGross)} muted />
            )}
            <SumRow label={t('parts_sales.vatAmount')} value={formatPriceWithCurrency(totals.vat)} muted />
            {totalDiscount > 0 && (
              <SumRow
                label={t('parts_sales.discountTotal')}
                value={`−${formatPriceWithCurrency(totalDiscount)}`}
                muted
              />
            )}
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography sx={{ fontWeight: 700 }}>{t('parts_sales.total')}</Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 700, ...numFont, letterSpacing: '-0.5px' }} data-testid="counter-sale-total">
                {formatPriceWithCurrency(totals.total)}
              </Typography>
            </Box>
            {cartMargin?.hasAny && (
              <Box sx={{ mt: 0.5 }} data-testid="counter-sale-margin">
                <SumRow
                  label={t('parts_sales.marginTotal')}
                  value={
                    cartMargin.percent != null
                      ? `${formatPriceWithCurrency(cartMargin.amount)} (${formatQuantity(cartMargin.percent)} %)`
                      : formatPriceWithCurrency(cartMargin.amount)
                  }
                  muted
                />
                {cartMargin.missingCost > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t('parts_sales.marginMissingCostRows', { count: cartMargin.missingCost })}
                  </Typography>
                )}
              </Box>
            )}
            <Button
              variant="contained"
              color="success"
              size="large"
              fullWidth
              startIcon={<PaymentsIcon />}
              disabled={!canSettle}
              onClick={() => setPayOpen(true)}
              data-testid="counter-sale-settle"
              sx={{ mt: 2, borderRadius: '10px', py: 1.25, fontWeight: 700 }}
            >
              {t('parts_sales.pay')}{canSettle ? ` · ${formatPriceWithCurrency(totals.total)}` : ''}
            </Button>
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <Kbd>F2</Kbd>
              <Typography variant="caption" color="text.secondary">{t('parts_sales.payShort')}</Typography>
              <Typography variant="caption" color="text.disabled">·</Typography>
              <Kbd>Esc</Kbd>
              <Typography variant="caption" color="text.secondary">{t('parts_sales.clearShort')}</Typography>
            </Box>
          </Paper>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 0.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 15, mt: 0.25, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary">
              {t('parts_sales.tillInfoNote')}
            </Typography>
          </Box>
        </Box>
      </Box>

      <PaymentDialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        total={totals.total}
        cartCount={settledCountRef.current || cartCount}
        terminals={terminalEnabled ? terminals : []}
        terminalId={terminalId}
        setTerminalId={setTerminalId}
        settle={settle}
        settleLeg={settleLeg}
        refundLeg={refundLeg}
        onVoid={voidPendingSale}
        pendingReceipt={pendingSale}
        onNewSale={startNewSale}
      />

      <QuickPickManageDialog
        open={managePicksOpen}
        onClose={() => setManagePicksOpen(false)}
        picks={quickPicks}
        onChanged={fetchQuickPicks}
      />

      {/* Register a new customer at the till — completely optional, one dialog. */}
      <CustomerDialog
        open={newCustomerOpen}
        handleClose={() => setNewCustomerOpen(false)}
        customer={null}
        addCreatedCustomer={(created) => {
          attachCustomer(created);
          setNewCustomerOpen(false);
        }}
        updateCustomer={() => {}}
      />

      <CustomerPurchaseHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        customerId={customer?.id}
        customerName={customer?.name}
      />

      {/* Edit a cart line (price / discount / qty / free-line margin scheme), or
          price a job that has no usable catalogue price before it enters the cart. */}
      <SaleLineDialog
        open={!!editLine || !!newJobLine}
        onClose={() => {
          setEditLine(null);
          setNewJobLine(null);
        }}
        initialLine={editLine || newJobLine}
        isNew={!!newJobLine}
        allowMargin
        customerId={customer?.id}
        onSubmit={(spec) => {
          if (newJobLine) {
            // Priced-in-dialog jobs are appended, not merged — two variable
            // jobs may legitimately carry different prices.
            const line = applySpecToLine(newJobLine, spec);
            setLines((prev) => [...prev, line]);
            flash(line.key);
            setNewJobLine(null);
            return;
          }
          setLines((prev) => prev.map((l) => (l.key === editLine.key ? applySpecToLine(l, spec) : l)));
          setEditLine(null);
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CounterSaleView;
